import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import portalApi from "../../api/portalClient";
import { Ticket } from "../../types";
import { useSort, sortRows, SortableTh } from "../../components/SortableTh";
import { formatDateTime } from "../../utils/datetime";
import { Breakpoint, useVisibleColumnCount } from "../../hooks/useVisibleColumns";

const STATUS_RANK: Record<string, number> = {
  open: 0,
  in_progress: 1,
  on_hold: 2,
  resolved: 3,
  closed: 4,
};
const PRIORITY_RANK: Record<string, number> = {
  low: 0,
  medium: 1,
  high: 2,
  urgent: 3,
};

const STATUS_LABEL: Record<string, string> = {
  open: "Open",
  in_progress: "In Progress",
  on_hold: "On Hold",
  resolved: "Resolved",
  closed: "Closed",
};
const STATUS_COLORS: Record<string, string> = {
  open: "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300",
  in_progress: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",
  on_hold: "bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300",
  resolved: "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300",
  closed: "bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-300",
};

function PriorityPill({ priority }: { priority: string }) {
  const colors: Record<string, string> = {
    urgent: "bg-red-500",
    high: "bg-orange-500",
    medium: "bg-yellow-500",
    low: "bg-gray-400",
  };
  const dot = colors[priority] || "bg-gray-400";
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={`w-1.5 h-1.5 rounded-full ${dot}`} />
      <span className="text-gray-700 dark:text-gray-300 capitalize text-xs">{priority}</span>
    </span>
  );
}

export default function PortalTickets() {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [includeClosed, setIncludeClosed] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", priority: "medium" });
  const [error, setError] = useState("");
  // colSpan is an ATTRIBUTE - no media query reaches it, so the empty-state
  // row needs the live count. Order and breakpoints MUST mirror the
  // SortableTh list below.
  const TICKET_COLUMNS: Breakpoint[] = [
    "base", // #
    "base", // Title
    "base", // Status
    "sm",   // Priority
    "md",   // Updated
  ];
  const ticketCols = useVisibleColumnCount(TICKET_COLUMNS);
  const ticketSort = useSort("updated_at", "desc");

  const load = () =>
    portalApi
      .get("/api/v1/portal/tickets", { params: { include_closed: includeClosed } })
      .then((r) => setTickets(r.data));
  useEffect(() => {
    load(); /* eslint-disable-next-line */
  }, [includeClosed]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    if (!form.title.trim()) {
      setError("Title is required");
      return;
    }
    try {
      const r = await portalApi.post("/api/v1/portal/tickets", {
        title: form.title.trim(),
        description: form.description.trim() || null,
        priority: form.priority,
      });
      setShowForm(false);
      setForm({ title: "", description: "", priority: "medium" });
      navigate(`/portal/tickets/${r.data.id}`);
    } catch (err: any) {
      setError(err.response?.data?.detail || "Failed to submit");
    }
  };

  return (
    <div className="max-w-6xl">
      <div className="flex items-center justify-between mb-2">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">My Tickets</h1>
        <button
          onClick={() => setShowForm(true)}
          className="bg-brand text-white text-sm px-5 py-2.5 rounded-lg hover:bg-brand-dark font-semibold shadow-sm transition-colors"
        >
          + Submit a Ticket
        </button>
      </div>
      <p className="text-sm text-gray-500 dark:text-gray-400 mb-5">
        Track open issues with the support team and submit new ones.
      </p>

      <label className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400 mb-3 cursor-pointer">
        <input
          type="checkbox"
          checked={includeClosed}
          onChange={(e) => setIncludeClosed(e.target.checked)}
          className="accent-brand"
        />
        Include closed tickets
      </label>

      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-hidden">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 dark:bg-gray-900/50 border-b border-gray-200 dark:border-gray-700">
            <tr>
              <SortableTh label="#" sortKey="id" sort={ticketSort.sort} onSort={ticketSort.toggle} />
              <SortableTh label="Title" sortKey="title" sort={ticketSort.sort} onSort={ticketSort.toggle} />
              <SortableTh label="Status" sortKey="status" sort={ticketSort.sort} onSort={ticketSort.toggle} />
              <SortableTh label="Priority" sortKey="priority" sort={ticketSort.sort} onSort={ticketSort.toggle} hide="hidden sm:table-cell" />
              <SortableTh label="Updated" sortKey="updated_at" sort={ticketSort.sort} onSort={ticketSort.toggle} hide="hidden md:table-cell" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
            {tickets.length === 0 && (
              <tr>
                <td colSpan={ticketCols} className="px-4 py-10 text-center text-gray-400 dark:text-gray-500">
                  No tickets yet. Click "Submit a Ticket" to open one.
                </td>
              </tr>
            )}
            {sortRows(tickets, ticketSort.sort, (t, key) => {
              switch (key) {
                case "id":
                  return t.id;
                case "title":
                  return (t.title || "").toLowerCase();
                case "status":
                  return STATUS_RANK[t.status] ?? 99;
                case "priority":
                  return PRIORITY_RANK[t.priority] ?? -1;
                case "updated_at":
                  return t.updated_at ? Date.parse(t.updated_at) : 0;
                default:
                  return 0;
              }
            }).map((t) => (
              <tr
                key={t.id}
                className="hover:bg-gray-50 dark:hover:bg-gray-700/40 cursor-pointer transition-colors"
                onClick={() => navigate(`/portal/tickets/${t.id}`)}
              >
                <td className="px-4 py-3 font-mono text-xs text-gray-500 dark:text-gray-400">#{t.id}</td>
                <td className="px-4 py-3 font-medium text-gray-900 dark:text-gray-100">
                  <span className="hover:text-brand dark:hover:text-brand-light dark:text-brand-light hover:underline">{t.title}</span>
                  {t.waiting_on_customer && (
                    <span className="ml-2 text-[10px] bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300 px-2 py-0.5 rounded-full font-medium">
                      Waiting on you
                    </span>
                  )}
                </td>
                <td className="px-4 py-3">
                  <span
                    className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[t.status]}`}
                  >
                    {STATUS_LABEL[t.status]}
                  </span>
                </td>
                <td className="px-4 py-3 capitalize text-xs hidden sm:table-cell">
                  <PriorityPill priority={t.priority} />
                </td>
                <td className="px-4 py-3 text-xs text-gray-500 dark:text-gray-400 hidden md:table-cell">
                  {formatDateTime(t.updated_at)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-md p-6 max-h-[90vh] overflow-y-auto">
            <h2 className="text-lg font-semibold mb-4 text-gray-900 dark:text-gray-100">Submit a Ticket</h2>
            <form onSubmit={submit} className="space-y-3">
              <div>
                <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">What's the issue?</label>
                <input
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="Short summary"
                  required
                  className="w-full border rounded-lg px-3 py-2 text-sm dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100"
                />
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Details</label>
                  <PortalDraftAssistButton
                    draft={`${form.title}\n\n${form.description}`}
                    onAppend={(qa) =>
                      setForm((f) => ({
                        ...f,
                        description: (f.description ? f.description + "\n\n" : "") + qa,
                      }))
                    }
                  />
                </div>
                <textarea
                  value={form.description}
                  rows={4}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="What's happening, what you've tried, when it started…"
                  className="w-full border rounded-lg px-3 py-2 text-sm dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">Priority</label>
                <select
                  value={form.priority}
                  onChange={(e) => setForm({ ...form, priority: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2 text-sm dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100"
                >
                  <option value="low">Low — non-urgent</option>
                  <option value="medium">Medium — needs attention this week</option>
                  <option value="high">High — affecting work today</option>
                </select>
              </div>
              {error && <p className="text-sm text-red-500">{error}</p>}
              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="border dark:border-gray-600 text-sm px-4 py-2 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/40"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="bg-brand hover:bg-brand-dark text-white text-sm px-5 py-2 rounded-lg font-semibold transition-colors shadow-sm"
                >
                  Submit
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function PortalDraftAssistButton({
  draft,
  onAppend,
}: {
  draft: string;
  onAppend: (qa: string) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [enabled, setEnabled] = useState<boolean | null>(null);
  const [questions, setQuestions] = useState<string[]>([]);
  const [answers, setAnswers] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    portalApi
      .get("/api/v1/portal/ai/status")
      .then((r) => setEnabled(!!r.data.enabled))
      .catch(() => setEnabled(false));
  }, []);

  if (!enabled) return null;

  const ask = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await portalApi.post("/api/v1/portal/ai/draft-assist", { draft });
      const qs: string[] = r.data.questions || [];
      setQuestions(qs);
      setAnswers(qs.map(() => ""));
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || "AI assist failed");
    } finally {
      setBusy(false);
    }
  };

  const append = () => {
    const block = questions
      .map((q, i) => `Q: ${q}\nA: ${answers[i] || "(no answer)"}`)
      .join("\n\n");
    onAppend(block);
    setQuestions([]);
    setAnswers([]);
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={ask}
        disabled={busy || draft.trim().length < 5}
        className="text-xs px-2 py-1 bg-violet-600 hover:bg-violet-700 text-white rounded font-medium disabled:opacity-50"
        title="Have AI ask 2-3 clarifying questions to help the tech"
      >
        âš¡ {busy ? "Thinking…" : "AI assist"}
      </button>
      {error && (
        <div className="mt-2 text-xs bg-red-50 dark:bg-red-900/40 text-red-800 dark:text-red-300 px-2 py-1 rounded">{error}</div>
      )}
      {questions.length > 0 && (
        <div className="absolute right-0 top-9 z-30 bg-white dark:bg-gray-800 border border-violet-300 dark:border-violet-700 rounded-lg shadow-lg p-3 w-80 space-y-2">
          <div className="text-xs font-semibold text-violet-700 dark:text-violet-300">
            A few quick questions:
          </div>
          {questions.map((q, i) => (
            <div key={i} className="space-y-1">
              <div className="text-xs font-medium text-gray-700 dark:text-gray-300">{q}</div>
              <input
                value={answers[i] || ""}
                onChange={(e) => {
                  const a = [...answers];
                  a[i] = e.target.value;
                  setAnswers(a);
                }}
                className="w-full border rounded px-2 py-1 text-xs dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100"
              />
            </div>
          ))}
          <div className="flex justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={() => setQuestions([])}
              className="text-xs px-2 py-1 border dark:border-gray-600 rounded text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/40"
            >
              Skip
            </button>
            <button
              type="button"
              onClick={append}
              className="text-xs px-2 py-1 bg-violet-600 hover:bg-violet-700 text-white rounded"
            >
              Add to details
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
